"use client";

import { cn } from "@workspace/ui/lib/utils";

const ASSISTANT_PLACEHOLDERS = [["w-full", "w-5/6"], ["w-2/3"]] as const;

/**
 * Message-shaped shimmer placeholders shown while a conversation hydrates —
 * the block's default whenever `<MagnusChat>` has no chat id yet (creation in
 * flight). Hosts that create chats themselves (so they can show the skeleton
 * before handing an id to the block) can render it directly:
 *
 * ```tsx
 * {chatId ? <MagnusChat chatId={chatId} ... /> : <ChatHydrationSkeleton />}
 * ```
 *
 * Mirrors the transcript's geometry: assistant turns are left-aligned text
 * columns, user turns are right-aligned rounded bubbles.
 */
export function ChatHydrationSkeleton({ className }: { className?: string }) {
  return (
    <div
      aria-busy="true"
      aria-label="Loading conversation"
      className={cn("flex h-full min-h-0 flex-col justify-end", className)}
      role="status"
    >
      <div className="mx-auto flex w-full max-w-3xl animate-pulse flex-col gap-6 px-4 py-6">
        <div className="flex flex-col gap-2 self-start">
          {ASSISTANT_PLACEHOLDERS[0].map((width) => (
            <div className={cn("h-3 rounded bg-muted", width)} key={width} />
          ))}
        </div>
        <div className="h-9 w-1/2 self-end rounded-lg bg-muted" />
        <div className="flex flex-col gap-2 self-start">
          {ASSISTANT_PLACEHOLDERS[1].map((width) => (
            <div className={cn("h-3 rounded bg-muted", width)} key={width} />
          ))}
        </div>
      </div>
    </div>
  );
}
